import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { setTitle } from "../../../helpers/MetaTag";
import Button from "../../../components/ui/Button";
import { getGift, giftIdFromSearch } from "../../../services/giftService";

/**
 * /gift/done?id=... Gift 4 (docs/design/HANDOFF.md: GiftDone, TabGiftDone,
 * DeskGiftDone): right after the recipient sends their gift books to print.
 * A recipient keeps their account, so this is an ordinary signed-in page
 * with a way back to My Books. The wording follows how many books there are
 * ("both books" for two, "all 3 books" for more).
 */
const GiftDone = () => {
  const location = useLocation();
  const giftId = giftIdFromSearch(location.search, "id");
  // null until the gift loads; a failed load falls back to the plural.
  const [count, setCount] = useState(giftId ? null : 2);

  useEffect(() => {
    if (!giftId) return;
    getGift(giftId).then((res) => setCount(res?.success ? (res.data.books || []).length || res.data.quantity : 2));
  }, [giftId]);

  const many = count === null ? true : count > 1;
  useEffect(() => {
    if (count === null) return;
    setTitle(`${many ? "Your books are on their way" : "Your book is on its way"} | MemoryDoodle`);
  }, [count, many]);

  return (
    <main className="mx-auto w-full max-w-[480px] px-6 pb-8 pt-14 md:max-w-[568px] md:pb-12 md:pt-[120px] lg:pb-16 lg:pt-24">
      <div className="flex flex-col gap-7">
        <div className="flex flex-col items-center gap-3.5 text-center">
          <span aria-hidden="true" className="mb-1.5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-green-100 text-green-800 md:h-[84px] md:w-[84px]">
            <Check className="h-8 w-8 md:h-9 md:w-9" strokeWidth={2.5} />
          </span>
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Thank you! We've got everything</h1>
          <p className="m-0 text-base leading-normal text-md-muted md:text-lg">
            {many
              ? "We'll print your coloring books and mail them together in one package. There's nothing else you need to do."
              : "We'll print your coloring book and mail it to you. There's nothing else you need to do."}
          </p>
        </div>
        <section aria-labelledby="gift-done-next" className="flex flex-col gap-4 rounded-2xl border border-md-pink-light-1 bg-white px-[22px] pb-6 pt-[22px]">
          <h2 id="gift-done-next" className="m-0 font-sans text-[13px] font-bold uppercase tracking-[0.06em] text-md-muted">
            What happens next
          </h2>
          <ol className="m-0 flex list-none flex-col gap-3.5 p-0">
            {[
              !many ? "We print and bind your book." : count === 2 ? "We print and bind both books." : count ? `We print and bind all ${count} books.` : "We print and bind all your books.",
              many ? "We mail them in one package to the address you confirmed." : "We mail it to the address you confirmed.",
            ].map((step, i) => (
              <li key={step} className="flex items-center gap-3.5">
                <span aria-hidden="true" className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-sm font-bold text-md-pink-darker md:h-[34px] md:w-[34px] lg:h-8 lg:w-8">
                  {i + 1}
                </span>
                <span className="text-base leading-[1.45] text-md-dark md:text-lg lg:text-[17px]">{step}</span>
              </li>
            ))}
          </ol>
        </section>
        <Button size="lg" fullWidth to="/dashboard" className="md:min-h-16 lg:min-h-[54px] lg:text-[17px]">
          Go to My Books
        </Button>
      </div>
    </main>
  );
};

export default GiftDone;
